import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAllServersRange } from '../api';
import { isOnlineStatus, toServerDateTime } from '../helpers';
import type { ServerStatsRow } from '../types';

function toDateInputValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function fromDateInputValue(s: string): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.valueOf()) ? null : d;
}

export function AllServersRangeTab() {
  const yesterday = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  }, []);
  const now = useMemo(() => new Date(), []);

  const [fromInput, setFromInput] = useState(toDateInputValue(yesterday));
  const [toInput, setToInput] = useState(toDateInputValue(now));
  const [committed, setCommitted] = useState({ from: '', to: '' });

  const all = useAllServersRange(
    committed.from,
    committed.to,
    Boolean(committed.from && committed.to),
  );

  const search = () => {
    const f = fromDateInputValue(fromInput);
    const t = fromDateInputValue(toInput);
    if (!f || !t) return;
    setCommitted({ from: toServerDateTime(f), to: toServerDateTime(t) });
  };

  const columns = useMemo<ColumnDef<ServerStatsRow>[]>(
    () => [
      { accessorKey: 'serverName', header: 'Server', size: 200 },
      { accessorKey: 'ip', header: 'IP', size: 130 },
      { accessorKey: 'reqDate', header: 'Date', size: 170 },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 100,
        cell: ({ row }) => {
          const online = isOnlineStatus(row.original.status);
          return (
            <span
              className={
                online
                  ? 'flex items-center gap-1 font-semibold text-emerald-700'
                  : 'flex items-center gap-1 font-semibold text-rose-700'
              }
            >
              ● {row.original.status}
            </span>
          );
        },
      },
      {
        accessorKey: 'cpuUsed',
        header: 'CPU %',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono">{row.original.cpuUsed}</span>
        ),
      },
      { accessorKey: 'memUsed', header: 'Mem Used', size: 100 },
      { accessorKey: 'memTotal', header: 'Mem Total', size: 100 },
      { accessorKey: 'diskUsed', header: 'Disk Used', size: 100 },
      { accessorKey: 'diskTotal', header: 'Disk Total', size: 100 },
      { accessorKey: 'load1m', header: 'Load 1m', size: 80 },
      {
        accessorKey: 'activeCalls',
        header: 'Calls',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-emerald-700">
            {(row.original.activeCalls ?? 0).toLocaleString()}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-[1fr_1fr_auto]">
        <div className="space-y-1.5">
          <Label className="text-xs">From</Label>
          <Input
            type="datetime-local"
            value={fromInput}
            onChange={(e) => setFromInput(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">To</Label>
          <Input
            type="datetime-local"
            value={toInput}
            onChange={(e) => setToInput(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs opacity-0">.</Label>
          <Button size="sm" onClick={search} disabled={all.isFetching}>
            <Search className="h-3.5 w-3.5" />
            Search
          </Button>
        </div>
      </div>

      {!committed.from ? (
        <div className="rounded-lg border border-dashed border-border bg-card/50 py-12 text-center text-sm text-muted-foreground">
          Pick a date range and click Search.
        </div>
      ) : (
        <DataTable<ServerStatsRow, unknown>
          tableId="monitoring:all:v1"
          columns={columns}
          data={all.data ?? []}
          isLoading={all.isFetching}
          getRowId={(row, i) => String(row.id ?? `${row.serverName}-${i}`)}
          pageSize={25}
          exportFilename="all-servers-range.csv"
          searchPlaceholder="Search…"
          emptyTitle="No rows"
          emptyDescription="No server data in this range."
        />
      )}
    </div>
  );
}
